import { type ReactNode, createContext, useContext, useState } from 'react';
import { toast } from 'react-toastify';
import type { Comment, Post, UserProfile } from 'src/types/social';
import { useAccount } from 'wagmi';
import { useProfile } from './ProfileContext';
import { SignatureContext } from './SignatureContext';

interface CommentState {
  [postId: number]: Comment[];
}

interface SocialContextType {
  userProfile: UserProfile | null;
  isProfileComplete: boolean;
  linkedWallets: string[];
  memberProfiles: UserProfile[];
  currentMemberProfile: UserProfile | null;
  refreshProfile: () => Promise<void>;
  fetchMemberProfile: (username: string) => Promise<void>;
  fetchMemberPosts: (username: string) => Promise<void>;
  memberPosts: Post[];
  isMemberLoading: boolean;
  memberError: string | null;
  handleFollow: (username: string) => Promise<void>;
  isFollowing: boolean;
  handleComment: (
    postId: number,
    content: string,
    images: (File | string)[],
  ) => Promise<Comment | false>;
  refreshComments: (postId: number) => Promise<void>;
  comments: CommentState;
  setComments: (
    comments: CommentState | ((prev: CommentState) => CommentState),
  ) => void;
  postingRestriction: {
    nextPostTime: Date | null;
    checkRestriction: () => Promise<void>;
  };
  createPost: (content: string, images: File[]) => Promise<boolean>;
}

const SocialContext = createContext<SocialContextType | null>(null);

export function SocialProvider({ children }: { children: ReactNode }) {
  const { address, isConnected } = useAccount();
  const { signature } = useContext(SignatureContext);
  const { profile, refreshProfile } = useProfile();
  const [memberProfiles, _setMemberProfiles] = useState<UserProfile[]>([]);
  const [currentMemberProfile, setCurrentMemberProfile] =
    useState<UserProfile | null>(null);
  const [memberPosts, setMemberPosts] = useState<Post[]>([]);
  const [isMemberLoading, setIsMemberLoading] = useState(false);
  const [memberError, setMemberError] = useState<string | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [comments, setComments] = useState<CommentState>({});
  const [nextPostTime, setNextPostTime] = useState<Date | null>(null);

  const isProfileComplete = (profile: UserProfile | null): boolean => {
    if (!profile) { return false; }
    return !!(
      profile.avatar_url &&
      profile.bio &&
      profile.telegram_handle &&
      profile.twitter_handle &&
      profile.bio.trim() !== '' &&
      profile.telegram_handle.trim() !== '' &&
      profile.twitter_handle.trim() !== ''
    );
  };

  const fetchMemberProfile = async (username: string) => {
    setIsMemberLoading(true);
    setMemberError(null);

    try {
      const headers: Record<string, string> = {};
      if (signature && address) {
        headers.Authorization = `Bearer ${signature}`;
        headers['x-address'] = address;
      }

      const profileResponse = await fetch(`/api/social/members/${username}`, {
        headers,
      });

      if (!profileResponse.ok) {
        if (profileResponse.status === 404) {
          setMemberError('Profile not found');
        } else if (profileResponse.status === 500) {
          setMemberError('Server error occurred');
        } else {
          setMemberError('Failed to load profile');
        }
        return;
      }

      const profileData = (await profileResponse.json()) as UserProfile;
      setCurrentMemberProfile(profileData);
      setIsFollowing(profileData.is_following || false);
    } catch (error) {
      console.error('Error fetching profile data:', error);
      setMemberError('Failed to load profile data');
      toast.error('Failed to load profile data');
    } finally {
      setIsMemberLoading(false);
    }
  };

  const fetchMemberPosts = async (username: string) => {
    try {
      const headers: Record<string, string> = {};
      if (signature && address) {
        headers.Authorization = `Bearer ${signature}`;
        headers['x-address'] = address;
      }

      const postsResponse = await fetch(
        `/api/social/members/${username}/posts`,
        { headers },
      );
      if (postsResponse.ok) {
        const postsData = (await postsResponse.json()) as Post[];
        setMemberPosts(postsData);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load posts');
    }
  };

  const handleFollow = async (username: string) => {
    if (!signature || !address) { return; }

    try {
      const response = await fetch(`/api/social/members/${username}/follow`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${signature}`,
          'x-address': address,
        },
      });

      if (response.ok) {
        setIsFollowing(!isFollowing);
        setCurrentMemberProfile((prev) =>
          prev
            ? {
                ...prev,
                followers_count: isFollowing
                  ? (prev.followers_count ?? 0) - 1
                  : (prev.followers_count ?? 0) + 1,
              }
            : null,
        );
        toast.success(
          isFollowing ? 'Unfollowed successfully' : 'Followed successfully',
        );
      }
    } catch (error) {
      console.error('Error following user:', error);
      toast.error('Failed to follow user');
    }
  };

  const handleComment = async (
    postId: number,
    content: string,
    images: (File | string)[],
  ) => {
    if (!signature || !address) { return; }

    const formData = new FormData();
    formData.append('content', content);

    // Handle both Files and meme URLs
    images.forEach((image, index) => {
      if (image instanceof File) {
        formData.append(`image${index}`, image);
      } else {
        formData.append(`memeUrl${index}`, image);
      }
    });

    try {
      const response = await fetch(`/api/social/posts/${postId}/comment`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${signature}`,
          'x-address': address,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to post comment');
      }

      const commentData = await response.json();
      await refreshComments(postId);
      return commentData;
    } catch (error) {
      console.error('Error posting comment:', error);
      toast.error('Failed to post comment');
      return false;
    }
  };

  const refreshComments = async (postId: number) => {
    if (!signature || !address) { return; }

    try {
      const response = await fetch(`/api/social/posts/${postId}/comment`, {
        headers: {
          Authorization: `Bearer ${signature}`,
          'x-address': address,
        },
      });

      if (response.ok) {
        const data: Comment[] = await response.json();
        setComments((prev: CommentState) => ({
          ...prev,
          [postId]: data,
        }));
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const checkPostingRestriction = async () => {
    if (!signature || !address) { return; }

    try {
      const response = await fetch('/api/social/posting-restriction', {
        headers: {
          Authorization: `Bearer ${signature}`,
          'x-address': address,
        },
      });

      const data = await response.json();
      if (data.nextPostTime) {
        const nextTime = new Date(data.nextPostTime);
        if (nextTime.getTime() > Date.now()) {
          setNextPostTime(nextTime);
        } else {
          setNextPostTime(null);
        }
      }
    } catch (error) {
      console.error('Error checking posting restriction:', error);
    }
  };

  const createPost = async (content: string, images: File[]) => {
    if (!signature || !address) { return false; }

    const formData = new FormData();
    formData.append('content', content);
    images.forEach((image, index) => {
      formData.append(`image${index}`, image);
    });

    try {
      const response = await fetch('/api/social/posts', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${signature}`,
          'x-address': address,
        },
        body: formData,
      });

      if (!response.ok) { throw new Error('Failed to create post'); }

      await checkPostingRestriction();
      toast.success('Post created successfully!');
      return true;
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error('Failed to create post');
      return false;
    }
  };

  const value = {
    userProfile: profile,
    isProfileComplete: isProfileComplete(profile),
    linkedWallets: [],
    memberProfiles,
    currentMemberProfile,
    memberPosts,
    isMemberLoading,
    memberError,
    isFollowing,
    refreshProfile,
    fetchMemberProfile,
    fetchMemberPosts,
    handleFollow,
    handleComment,
    refreshComments,
    comments,
    setComments,
    postingRestriction: {
      nextPostTime,
      checkRestriction: checkPostingRestriction,
    },
    createPost,
  };

  return (
    <SocialContext.Provider value={value}>{children}</SocialContext.Provider>
  );
}

export const useSocial = () => {
  const context = useContext(SocialContext);
  if (!context) {
    throw new Error('useSocial must be used within a SocialProvider');
  }
  return context;
};
